/* @flow */

import React from 'react'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import type { Poll, Tag } from '../../types'

import { openSnippetEditor } from '../../stores/pollBuilderIconPicker'

import {
  newMinLengthValidator,
  newMaxLengthValidator,
  pollVerticalValidator,
} from '../../helpers'
import Condition from '../Condition'
import PollFormToggles from './PollFormToggles'
import PollSketch from '../PollSketch'
import IfAdmin from '../IfAdmin'
import CustomerSelect from '../CustomerSelect/'
import { MIN_POLL_LABEL_LENGTH, MAX_POLL_LABEL_LENGTH } from '../CheckedoutPoll'

import Controls from './Controls'
import OpinaryInput from '../Controls/OpinaryInput'
import TagsSelect from './TagsSelect'

import {
  updateCheckedoutPoll,
  getPollCustomerNicheAutomation,
  selectCheckedoutPoll,
} from '../../stores/pollBuilderCheckedoutPoll'
import { setSelectedExpertOpinion } from '../../stores/pollBuilderSelectedExpertOpinion'
import { getTags } from '../../stores/tags'
import {
  selectCustomerById,
  selectCustomerVerticals,
} from '../../stores/customersStore'

export function OneAxisForm({
  poll,
  tags,
  updatePoll,
  setSelectedExpertOpinion,
  isAdmin,
  niche,
  openSnippetEditor,
  customerVerticals,
  customerConfig,
}: {
  poll: Poll,
  tags: Array<Tag>,
  updatePoll: Object => void,
  setSelectedExpertOpinion: Object => void,
  openSnippetEditor: () => void,
  isAdmin: boolean,
  niche: boolean,
  customerVerticals: Array<Object>,
  customerConfig: Object,
}) {
  function setPollAttribute(key, value) {
    updatePoll({
      [key]: value,
    })
  }

  function makeChangeHandler(key: string) {
    return (event: Event) => {
      if (event.target.value == null) {
        throw new Error('expected event.target.value to exist')
      }
      setPollAttribute(key, event.target.value)
    }
  }

  function updateName(event) {
    const { value } = event.target
    if (!value) {
      setPollAttribute('userEnteredId', false)
    } else {
      setPollAttribute('userEnteredId', true)
    }
    setPollAttribute('name', event.target.value)
  }

  function setCustomerId(customer) {
    setPollAttribute('customer', customer)
  }

  const minError = newMinLengthValidator(MIN_POLL_LABEL_LENGTH)
  const maxError = newMaxLengthValidator(MAX_POLL_LABEL_LENGTH)

  const verticalsError = pollVerticalValidator(
    poll,
    customerVerticals,
    customerConfig.enableVerticalTargeting
  )

  const isFormValid = () => {
    return (
      verticalsError == null &&
      minError(poll.header) == null &&
      minError(poll.labelLeft) == null &&
      minError(poll.labelRight) == null &&
      maxError(poll.header) == null &&
      maxError(poll.labelLeft) == null &&
      maxError(poll.labelRight) == null
    )
  }

  const handleIconClick = clickedIcon => {
    setSelectedExpertOpinion(clickedIcon)
    openSnippetEditor()
  }

  const handlerBlur = event => {
    let name = event.target.name

    event.target.name && event.target.value
      ? updatePoll({ [name]: false })
      : updatePoll({ [name]: true })
  }

  return (
    <form>
      <div className="w-100 relative mb4 flex">
        <span className="bg-op-con-dark op-con-light f6 ph4 h3 flex items-center">
          <span className="nowrap">Speedometer</span>
        </span>
        <span className="w-60 mh4 mr-auto">
          <IfAdmin>
            <CustomerSelect
              className="w-100 bb b--moon-gray"
              colorClass="gray"
              onChange={setCustomerId}
              customerId={poll.customer}
            />
          </IfAdmin>
        </span>
        <span className="f6 bg-near-white gray ph4 h3 flex items-center">
          <span>Example</span>
        </span>
      </div>

      <div className="w-100 flex flex-column items-center">
        <div className="w-90">
          <OpinaryInput
            placeholder="Question"
            onChange={makeChangeHandler('header')}
            value={poll.header}
            className="w-100 mb2"
            inputClass="tc"
            colorClass="dark-gray"
            borderClass={maxError(poll.header) ? 'b--dark-red' : 'b--moon-gray'}
            maxLength={MAX_POLL_LABEL_LENGTH + 1}
            onBlur={handlerBlur}
            name="questionInput"
          />

          <Condition shouldRender={poll.questionInput ? true : false}>
            <div className="tl dark-red"> Field cannot be empty </div>
          </Condition>
          <Condition shouldRender={maxError(poll.header) != null}>
            <div className="tl dark-red">{maxError(poll.header)}</div>
          </Condition>

          <div className="w-100 tc">
            <PollSketch poll={poll} onIconClick={handleIconClick} />
          </div>

          <div className="cf mt4">
            <div className="fl w-40">
              <div className="w-100 mb4">
                <OpinaryInput
                  id="labelLeft"
                  placeholder="Left label"
                  onChange={makeChangeHandler('labelLeft')}
                  value={poll.labelLeft}
                  className="w-100 mb2"
                  colorClass="dark-gray"
                  borderClass={
                    maxError(poll.labelLeft) ? 'b--dark-red' : 'b--moon-gray'
                  }
                  maxLength={MAX_POLL_LABEL_LENGTH + 1}
                  onBlur={handlerBlur}
                  name="labelFieldLeft"
                />
                <Condition shouldRender={maxError(poll.labelLeft) != null}>
                  <div className="dark-red">{maxError(poll.labelLeft)}</div>
                </Condition>
                <Condition shouldRender={poll.labelFieldLeft ? true : false}>
                  <div className="tl dark-red"> Field cannot be empty </div>
                </Condition>
              </div>
            </div>

            <div className="fr w-40">
              <div className="tr w-100 mb4">
                <OpinaryInput
                  id="labelRight"
                  placeholder="Right label"
                  value={poll.labelRight}
                  onChange={makeChangeHandler('labelRight')}
                  className="w-100 mb2"
                  inputClass="tr"
                  colorClass="dark-gray"
                  borderClass={
                    maxError(poll.labelRight) ? 'b--dark-red' : 'b--moon-gray'
                  }
                  maxLength={MAX_POLL_LABEL_LENGTH + 1}
                  onBlur={handlerBlur}
                  name="labelFieldRight"
                />
                <Condition shouldRender={maxError(poll.labelRight) != null}>
                  <div className="tl dark-red">{maxError(poll.labelRight)}</div>
                </Condition>
                <Condition shouldRender={poll.labelFieldRight ? true : false}>
                  <div className="tl dark-red"> Field cannot be empty </div>
                </Condition>
              </div>
            </div>
          </div>

          <label className="f6 gray db">Poll ID</label>
          {poll.id ? (
            <span className="gray mt1">{poll.name}</span>
          ) : (
            <OpinaryInput
              id="name"
              onChange={updateName}
              value={poll.name}
              className="w-40 f6"
            />
          )}

          <TagsSelect
            options={tags}
            className="w-40 mt4"
            value={poll.tags}
            onChange={val => setPollAttribute('tags', val)}
          />
        </div>
        <div className="w-90 flex mb4">
          <PollFormToggles
            poll={poll}
            toggleAttribute={setPollAttribute}
            isAdmin={isAdmin}
            canAddToLib={niche} // todo: update the value
          />
        </div>
      </div>

      <div className="w-100 h3 bg-near-white flex items-center justify-center">
        <div className="w-90">
          <Controls poll={poll} isFormValid={isFormValid()} />
        </div>
      </div>
    </form>
  )
}

const mapStateToProps = state => {
  const poll = selectCheckedoutPoll(state)
  return {
    tags: getTags(state),
    niche: getPollCustomerNicheAutomation(state),
    customerVerticals: selectCustomerVerticals(state),
    customerConfig: poll ? selectCustomerById(state, poll.customer) : {},
  }
}
export default compose(
  connect(
    mapStateToProps,
    {
      updatePoll: updateCheckedoutPoll,
      setSelectedExpertOpinion: setSelectedExpertOpinion,
      openSnippetEditor,
    }
  )
)(OneAxisForm)



// WEBPACK FOOTER //
// ./src/components/PollForm/OneAxisPollForm.js